import { brandColor } from '../../../_helpers/brand-color';
import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import {
  CalendarDateFormatter,
  CalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarModule,
  CalendarView,
  DAYS_OF_WEEK,
} from 'angular-calendar';
import { Subject, Subscription, forkJoin, map, of } from 'rxjs';
import { SitesService } from '../../../_services/sites/sites.service';
import { DialogService } from 'primeng/dynamicdialog';
import { FormCreateAppointmentComponent } from '../../forms/form-create-appointment/form-create-appointment.component';
import { AppointmentComponent } from '../../../components/appointment/appointment.component';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AppointmentService } from '../../../_services/appointments/appointment.service';
import { CalendarAppointmentsService } from '../../../_services/calendars/calendar-appointments.service';
import { CalendarTimeBlocksService } from '../../../_services/calendars/calendar-time-blocks.service';
import ICalendar from '../../../_interfaces/ICalendar';
import IAppointment from '../../../_interfaces/IAppointment';
import ICalendarTimeBlock from '../../../_interfaces/ICalendarTimeBlock';
import IPatientHasTreatment from '../../../_interfaces/IPatientHasTreatment';
import IPatient from '../../../_interfaces/IPatients';
import ITimetable from '../../../_interfaces/ITimetable';
import { CustomDateFormatter } from '../../../_providers.ts/custom-date-formatter.provider';
import { PatientsService } from '../../../_services/patients/patients.service';
import ICalendarNote from '../../../_interfaces/ICalendarNote';
import { LoadingService } from '../../../_services/loading/loading.service';
import { CalendarNotesService } from '../../../_services/calendar-notes/calendar-notes.service';
import { BaseComponent } from '../../../components/base/base.component';
import { SessionService } from '../../../_services/session/session.service';
import { Calendar } from 'primeng/calendar';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { PopoverModule } from 'primeng/popover';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CalendarAppointmentsActionsComponent } from './calendar-appointments-actions/calendar-appointments-actions.component';
import { CalendarAppointmentsViewComponent } from './calendar-appointments-view/calendar-appointments-view.component';
import { FormCalendarNoteComponent } from '../../forms/form-calendar-note/form-calendar-note.component';
import {
  IAppointmentWarning,
  WarningSeverity,
  APPOINTMENT_WARNINGS,
  getMaxSeverity,
  sortWarningsBySeverity,
} from '../../../_interfaces/IAppointmentWarning';
import {
  ICalendarSettings,
  applyFilters,
  createDefaultSettings,
} from '../../../_interfaces/ICalendarSettings';
import { ErrorsService } from '../../../_services/errors/errors.service';

@Component({
  standalone: true,
  selector: 'app-calendar-appointments',
  templateUrl: './calendar-appointments.component.html',
  styleUrls: ['./calendar-appointments.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [
    CalendarModule,
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    PopoverModule,
    TranslateModule,
    CalendarAppointmentsActionsComponent,
    CalendarAppointmentsViewComponent,
  ],
  providers: [
    { provide: CalendarDateFormatter, useClass: CustomDateFormatter },
  ],
  styles: [
    `
      .cal-week-view .cal-time-events .cal-day-column {
        margin-right: 0px;
      }

      .cal-week-view .cal-hour {
        width: calc(100% + 0px);
      }

      .cal-week-view {
        .cal-hour-segment.cal-after-hour-start .cal-time {
          display: block;
        }
      }

      .cal-event {
        border-left-width: 5px !important;
        border-left-style: solid !important;
        border-top: none !important;
        border-right: none !important;
        border-bottom: none !important;
      }
    `,
  ],
})
export class CalendarAppointmentsComponent
  extends BaseComponent
  implements OnInit, OnDestroy, OnChanges
{
  private static readonly TAP_DRAG_THRESHOLD_PX = 8;
  private static readonly DESKTOP_SELECTION_EVENT_ID = '__desktop-selection__';

  @ViewChild('calendar') calendar!: Calendar;
  @Input('calendar') calendars: ICalendar[];
  @Input('view') view: CalendarView = CalendarView.Week;
  @Input('date') date: string = undefined;

  @Input('patient')
  public patient?: IPatient = undefined;
  @Input('treatments')
  public treatments?: IPatientHasTreatment;
  @Input('quickMode')
  public quickMode: boolean = false;

  @Input('settings')
  public settings: ICalendarSettings = createDefaultSettings();
  @Input('openSettings')
  public openSettings: (() => void) | undefined;
  public CalendarView = CalendarView;
  public viewDate: Date = moment().toDate();
  public isMobileView =
    typeof window !== 'undefined' && window.innerWidth <= 768;

  public refresh: Subject<void> = new Subject();

  public dayStartHour = 8;
  public weekStartsOn = DAYS_OF_WEEK.MONDAY;
  public calendarModeSelector: any[];
  public activeDayIsOpen: boolean = false;

  public excludeDays: number[] = [0];

  public locale: string;
  public subscriptionCalendar: Subscription;
  public subscriptionAppointments: Subscription;
  public subscriptionTranslate: Subscription;

  public notes: CalendarEvent[] = [];
  public timeBlocks: CalendarEvent[] = [];
  public appointments: CalendarEvent[] = [];
  public events: CalendarEvent[] = [];
  public selectionDialogVisible = false;
  public timeBlockDialogVisible = false;
  public selectedRangeStart: Date | null = null;
  public selectedRangeEnd: Date | null = null;
  public selectedRangeCalendarIds: number[] = [];
  public selectedRangeLocksDuration = false;
  public timeBlockReason = '';
  public timeBlockNotes = '';
  public savingTimeBlock = false;
  public editTimeBlockDialogVisible = false;
  public editingTimeBlock: ICalendarTimeBlock | null = null;
  public editingTimeBlockCalendarId: number | null = null;
  public editingTimeBlockReason = '';
  public editingTimeBlockNotes = '';
  public savingEditedTimeBlock = false;
  public deletingTimeBlock = false;
  private desktopSelectionEvent: CalendarEvent | null = null;
  private desktopPointerState:
    | {
        pointerId: number;
        startDate: Date;
        startY: number;
        segmentHeight: number;
        dragging: boolean;
        moved: boolean;
        calendar?: ICalendar;
        target: HTMLElement | null;
      }
    | null = null;

  public isReady = false;

  /**
   * Detecta si estamos en un dispositivo móvil basándose en el ancho de pantalla
   */
  private isMobileDevice(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  /**
   * Obtiene el número de días a mostrar en la vista de semana
   * Desktop: undefined (usa semana completa respetando weekStartsOn)
   */
  public getDaysInWeek(): number | undefined {
    return undefined;
  }

  /**
   * Calcula los días a excluir basándose en la configuración
   */
  public getExcludeDays(): number[] {
    // Si showSundays está activo, no excluir domingos
    if (this.settings.view.showSundays) {
      return this.excludeDays.filter((day) => day !== 0);
    }
    // Si no está incluido el domingo, añadirlo
    if (!this.excludeDays.includes(0)) {
      return [...this.excludeDays, 0];
    }
    return this.excludeDays;
  }

  /**
   * Obtiene la hora de inicio del día basándose en showFullDay
   */
  public getDayStartHour(): number {
    return this.settings.view.showFullDay ? 0 : this.settings.view.dayStartHour;
  }

  /**
   * Obtiene la hora de fin del día basándose en showFullDay
   */
  public getDayEndHour(): number {
    return this.settings.view.showFullDay ? 24 : this.settings.view.dayEndHour;
  }

  /**
   * Calcula la duración de un evento en formato legible
   */
  public getEventDuration(event: CalendarEvent): string {
    if (!event.start || !event.end) return '';
    const start = moment(event.start);
    const end = moment(event.end);
    const diffMinutes = end.diff(start, 'minutes');

    if (diffMinutes < 60) {
      return `${diffMinutes}m`;
    }

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (minutes === 0) {
      return `${hours}h`;
    }
    return `${hours}h ${minutes}m`;
  }

  constructor(
    public patientsService: PatientsService,
    public siteService: SitesService,
    public session: SessionService,
    public calendarNotesService: CalendarNotesService,
    public dialogService: DialogService,
    private appointmentsService: AppointmentService,
    private translate: TranslateService,
    private loadingService: LoadingService,
    private calendarAppointmentsService: CalendarAppointmentsService,
    private calendarTimeBlocksService: CalendarTimeBlocksService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private errorsService: ErrorsService,
  ) {
    super(session);
  }

  private hasAppointmentDragPermission(): boolean {
    return this.hasPermission('APPOINTMENTS.UPDATE');
  }

  private hasNoteDragPermission(): boolean {
    return this.hasPermission('CALENDAR_NOTES.UPDATE');
  }

  private canDragAppointment(appointment?: IAppointment | null): boolean {
    return !!appointment && this.hasAppointmentDragPermission();
  }

  private canDragNote(note?: ICalendarNote | null): boolean {
    return !!note && this.hasNoteDragPermission();
  }

  private canResizeNote(note?: ICalendarNote | null): boolean {
    return (
      this.canDragNote(note) &&
      !!note?.start &&
      !!note?.end
    );
  }

  private updateEventLocally(
    event: CalendarEvent,
    start: Date,
    end?: Date | null,
  ): void {
    event.start = start;
    event.end = end || undefined;
    this.events = [...this.events];
    this.refresh.next();
  }

  private hasSameSchedule(
    previousStart: Date,
    previousEnd: Date | undefined,
    nextStart: Date,
    nextEnd: Date | undefined,
  ): boolean {
    const previousStartTime = previousStart?.getTime?.() ?? null;
    const previousEndTime = previousEnd?.getTime?.() ?? null;
    const nextStartTime = nextStart?.getTime?.() ?? null;
    const nextEndTime = nextEnd?.getTime?.() ?? null;

    return previousStartTime === nextStartTime && previousEndTime === nextEndTime;
  }

  private buildAppointmentDatePayload(
    start: Date,
    end: Date,
  ): { start: string; end: string } {
    return {
      start: moment(start).toISOString(),
      end: moment(end).toISOString(),
    };
  }

  private buildNoteDatePayload(
    note: ICalendarNote,
    start: Date,
    end?: Date | null,
  ): ICalendarNote {
    const hasHours = !!note?.start && !!note?.end;

    return {
      calendars_id: note.calendars_id,
      title: note.title,
      description: note.description,
      date: moment(start)
        .hour(0)
        .minute(0)
        .seconds(0)
        .millisecond(0)
        .utc()
        .toDate(),
      start:
        hasHours && start
          ? moment(start).utc().format('HH:mm')
          : undefined,
      end:
        hasHours && end
          ? moment(end).utc().format('HH:mm')
          : undefined,
    };
  }

  private translateOrFallback(key: string, fallback: string): string {
    const translated = this.translate.instant(key);
    return translated && translated !== key ? translated : fallback;
  }

  private showAppointmentDragSuccess(): void {
    this.messageService.add({
      detail: this.translate.instant('MESSAGES.APPOINTMENTS.UPDATE.DATE.DETAIL'),
      summary: this.translate.instant('MESSAGES.APPOINTMENTS.UPDATE.DATE.SUMMARY'),
      severity: 'success',
    });
  }

  private showAppointmentDragError(error: any): void {
    const code = this.errorsService.getMessageFromAppointmentError(error);
    this.messageService.add({
      detail: this.translate.instant(`MESSAGES.APPOINTMENTS.ERROR.${code}`),
      summary: this.translate.instant('MESSAGES.APPOINTMENTS.ERROR.SUMMARY'),
      severity: 'error',
    });
  }

  private showNoteDragSuccess(): void {
    this.messageService.add({
      detail: this.translateOrFallback(
        'MESSAGES.SUCCESS.UPDATE.CALENDAR_NOTES.DETAIL',
        'La nota del calendario ha sido actualizada con éxito',
      ),
      summary: this.translateOrFallback(
        'MESSAGES.SUCCESS.UPDATE.CALENDAR_NOTES.SUMMARY',
        'Nota del calendario actualizada',
      ),
      severity: 'success',
    });
  }

  private showNoteDragError(error: any): void {
    this.errorsService.getMessageFromCalendarError(error);
    this.messageService.add({
      detail: this.translateOrFallback(
        'MESSAGES.ERROR.UPDATE.CALENDAR_NOTES.DETAIL',
        'Hubo un error al actualizar la nota del calendario, por favor, intente nuevamente',
      ),
      summary: this.translateOrFallback(
        'MESSAGES.ERROR.UPDATE.CALENDAR_NOTES.SUMMARY',
        'Error al actualizar la nota del calendario',
      ),
      severity: 'error',
    });
  }

  public eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    const previousStart = event.start;
    const previousEnd = event.end;

    if ((event as any).appointment) {
      const appointment = (event as any).appointment as IAppointment;
      if (!this.canDragAppointment(appointment)) {
        return;
      }

      const nextEnd =
        newEnd ||
        (previousEnd
          ? moment(newStart)
              .add(moment(previousEnd).diff(moment(previousStart), 'minutes'), 'minutes')
              .toDate()
          : previousEnd);

      if (!nextEnd) {
        return;
      }

      if (this.hasSameSchedule(previousStart, previousEnd, newStart, nextEnd)) {
        return;
      }

      this.updateEventLocally(event, newStart, nextEnd);
      this.appointmentsService
        .updateDate(
          this.customers_id,
          this.site.id,
          appointment.id!,
          this.buildAppointmentDatePayload(newStart, nextEnd),
        )
        .subscribe({
          next: () => {
            this.showAppointmentDragSuccess();
            this.loadCalendarItems({ appointments: true });
          },
          error: (error) => {
            this.updateEventLocally(event, previousStart, previousEnd);
            this.showAppointmentDragError(error);
          },
        });
      return;
    }

    if ((event as any).note) {
      const note = (event as any).note as ICalendarNote;
      if (!this.canDragNote(note)) {
        return;
      }

      const nextEnd = this.canResizeNote(note)
        ? newEnd ||
          (previousEnd
            ? moment(newStart)
                .add(
                  moment(previousEnd).diff(moment(previousStart), 'minutes'),
                  'minutes',
                )
                .toDate()
            : previousEnd)
        : undefined;

      if (this.hasSameSchedule(previousStart, previousEnd, newStart, nextEnd)) {
        return;
      }

      this.updateEventLocally(event, newStart, nextEnd);
      this.calendarNotesService
        .update(
          this.customers_id,
          this.site.id,
          note.id!,
          this.buildNoteDatePayload(note, newStart, nextEnd),
        )
        .subscribe({
          next: () => {
            this.showNoteDragSuccess();
            this.loadCalendarItems({ notes: true });
          },
          error: (error) => {
            this.updateEventLocally(event, previousStart, previousEnd);
            this.showNoteDragError(error);
          },
        });
    }
  }

  private isExpiredPortalPendingRequest(
    appointment?: IAppointment | null,
    now: moment.Moment = moment(),
  ): boolean {
    if (!appointment) return false;
    if (appointment.source !== 'PATIENT_PORTAL') return false;
    if (appointment.approval_state !== 'PENDING_APPROVAL') return false;
    if (!appointment.end) return false;
    return moment(appointment.end).isBefore(now);
  }

  public onAction = (config: {
    date?: Date;
    view?: CalendarView;
    retrieve: { appointments: boolean; notes: boolean };
  }) => {
    if (config.date) {
      this.viewDate = config.date;
    }

    if (config.view) {
      this.view = config.view;
    }
    this.loadCalendarItems(config.retrieve);
  };

  /**
   * Calcula los warnings de una cita
   */
  public getAppointmentWarnings(
    appointment: IAppointment,
  ): IAppointmentWarning[] {
    const warnings: IAppointmentWarning[] = [];

    // Sin paciente asignado
    if (!appointment.patient) {
      warnings.push({
        ...APPOINTMENT_WARNINGS['NO_PATIENT'],
        label: this.translate.instant('WARNINGS.NO_PATIENT'),
        action: this.translate.instant('WARNINGS.ACTION_ASSIGN_PATIENT'),
      });
    }

    // Sin tratamiento asignado
    if (!appointment.treatments || appointment.treatments.length === 0) {
      warnings.push({
        ...APPOINTMENT_WARNINGS['NO_TREATMENT'],
        label: this.translate.instant('WARNINGS.NO_TREATMENT'),
        action: this.translate.instant('WARNINGS.ACTION_ASSIGN_TREATMENT'),
      });
    }

    // Pago pendiente
    const isPaid = this.appointmentsService.isPaid(appointment);
    if (!isPaid) {
      warnings.push({
        ...APPOINTMENT_WARNINGS['NOT_PAID'],
        label: this.translate.instant('WARNINGS.NOT_PAID'),
        action: this.translate.instant('WARNINGS.ACTION_PAY'),
      });
    }

    return sortWarningsBySeverity(warnings);
  }

  public appointmentToCalendar = (appointment: IAppointment) => {
    if (this.isExpiredPortalPendingRequest(appointment)) {
      return null;
    }

    const isCompleted =
      appointment.patient != null && appointment.treatments.length > 0;

    const isPaid = this.appointmentsService.isPaid(appointment);
    const isPatientCompleted = this.patientsService.isPatientCompleted(
      appointment.patient,
    );

    const title = this.getAppointmentTitle(appointment);

    // Obtener colores según el modo configurado
    const treatmentColor = this.getTreatmentColor(appointment);
    const statusColor = appointment.status?.bgcolor || '#E0E0E0';

    // Seleccionar color según el modo
    const cardColor =
      this.settings.display.colorMode === 'status'
        ? statusColor
        : treatmentColor;

    // El borde siempre muestra el estado para diferenciarlo
    const borderColor =
      this.settings.display.colorMode === 'status'
        ? treatmentColor // Si el fondo es estado, el borde muestra tratamiento
        : statusColor; // Si el fondo es tratamiento, el borde muestra estado

    // Calcular warnings
    const warnings = this.getAppointmentWarnings(appointment);
    const maxSeverity = getMaxSeverity(warnings);

    // Verificar si la cita es pasada
    const isPastEvent = moment(appointment.end).isBefore(moment());
    const isPortalPending =
      appointment.source === 'PATIENT_PORTAL' &&
      appointment.approval_state === 'PENDING_APPROVAL';

    return {
      appointment,
      start: moment(appointment.start).toDate(),
      end: moment(appointment.end).toDate(),
      title,
      color: {
        primary: cardColor,
        secondary: cardColor,
      },
      cssClass: `Status Status__id_${appointment.status.id}${
        warnings.length > 0 ? ' has-warnings' : ''
      }${maxSeverity ? ` severity-${maxSeverity}` : ''}${
        !!!isPaid ? ' Status__NotPaid' : ''
      }${!!!isCompleted ? ' Status__incompleted' : ''}${
        isPastEvent ? ' past-event' : ''
      }${isPortalPending ? ' Status__portal-request' : ''}
      }`,
      meta: {
        statusBorderColor: borderColor,
        warnings: warnings,
        maxSeverity: maxSeverity,
        isPast: isPastEvent,
      },
      draggable: this.canDragAppointment(appointment),
      resizable: {
        beforeStart: this.canDragAppointment(appointment),
        afterEnd: this.canDragAppointment(appointment),
      },
    };
  };

  private getTreatmentColor(appointment: IAppointment): string {
    // Si tiene tratamientos, usar el color del primer tratamiento
    if (appointment.treatments && appointment.treatments.length > 0) {
      const firstTreatment = appointment.treatments[0];
      if (firstTreatment.treatment?.color) {
        return firstTreatment.treatment.color;
      }
    }
    // Si no tiene tratamiento, usar un color gris por defecto
    return '#E0E0E0';
  }

  private getAppointmentTitle(appointment: IAppointment): string {
    // Si tiene name, se usa directamente
    if (appointment.name && appointment.name.trim() !== '') {
      return appointment.name;
    }

    const parts: string[] = [];

    // Agregar nombre del paciente si existe
    if (appointment.patient) {
      const patientName = `${appointment.patient.name || ''} ${
        appointment.patient.surname || ''
      }`.trim();
      if (patientName) {
        parts.push(patientName);
      }
    }

    // Agregar tratamientos si existen
    if (appointment.treatments && appointment.treatments.length > 0) {
      const treatmentNames = appointment.treatments
        .map((t) => t.treatment?.name || '')
        .filter((name) => name.trim() !== '')
        .join(', ');

      if (treatmentNames) {
        if (parts.length > 0) {
          parts.push(`: ${treatmentNames}`);
        } else {
          parts.push(treatmentNames);
        }
      }
    }

    // Si no hay información, devolver mensaje traducido
    if (parts.length === 0) {
      return this.translate.instant('APPOINTMENT.INCOMPLETE');
    }

    return parts.join('');
  }

  public getPrecision() {
    return Math.min(...this.calendars.map((x) => x.precision), 30);
  }

  public setSchedule() {
    const excludedDaysFromEachCalendar = this.calendars.map((calendar) => {
      return calendar.timetables
        .filter((t: ITimetable) => t.active == 0)
        .map((t: ITimetable) => t.weekdays_id);
    });

    this.excludeDays = excludedDaysFromEachCalendar.reduce(
      (commonExcludedDays, currentExcludedDays) => {
        return commonExcludedDays.filter((day) =>
          currentExcludedDays.includes(day),
        );
      },
    );
  }

  public getDates() {
    let mode = this.view;
    const start = moment(this.viewDate).startOf(mode);
    const end = moment(this.viewDate).endOf(mode);
    return {
      start: start.utc().format('DD-MM-YYYY HH:mm:ss'),
      end: end.utc().format('DD-MM-YYYY HH:mm:ss'),
    };
  }

  public loadAppointments = () => {
    const options = this.getDates();

    if (this.calendars.length == 0) {
      return of([]);
    }
    const observables = this.calendars.map((calendar) => {
      return this.appointmentsService.getAppointmentsByCalendar(
        this.customers_id,
        this.site,
        calendar,
        options,
      );
    });

    return forkJoin(observables).pipe(
      map((matrix: IAppointment[][]) => {
        this.isReady = true;
        const appointments = matrix.flat();

        // Aplicar sistema de filtros desde settings
        const filteredAppointments = applyFilters(
          appointments || [],
          this.settings.filters,
        );

        return filteredAppointments
          .map(this.appointmentToCalendar)
          .filter((event) => !!event) as CalendarEvent[];
      }),
    );
  };

  public loadNotes = () => {
    const options = this.getDates();

    if (this.calendars.length == 0) {
      return of([]);
    }
    const observables = this.calendars.map((calendar) => {
      return this.calendarNotesService.getByCalendar(
        this.customers_id,
        this.site,
        calendar,
        options,
      );
    });

    return forkJoin(observables).pipe(
      map((matrix: ICalendarNote[][]) => {
        const notes = matrix.flat();

        return (notes || []).map(this.notesToCalendar);
      }),
    );
  };

  public loadTimeBlocks = () => {
    const options = this.getDates();

    if (!this.calendars?.length || !this.site?.id) {
      return of([]);
    }

    return this.calendarTimeBlocksService
      .listBySite(this.customers_id, this.site, {
        calendars_ids: this.calendars.map((calendar) => Number(calendar.id)),
        start: options.start,
        end: options.end,
      })
      .pipe(
        map((blocks: ICalendarTimeBlock[]) =>
          (blocks || []).map(this.timeBlockToCalendar),
        ),
      );
  };

  public refreshCalendar = () => {
    this.events = [
      ...this.appointments,
      ...this.notes,
      ...this.timeBlocks,
      ...(this.desktopSelectionEvent ? [this.desktopSelectionEvent] : []),
    ];
    this.refresh.next();
  };

  @HostListener('window:resize')
  public onWindowResize(): void {
    const nextMobile = this.isMobileDevice();
    if (nextMobile !== this.isMobileView) {
      this.isMobileView = nextMobile;
      if (this.isMobileView && this.view === CalendarView.Month) {
        this.view = CalendarView.Week;
      }
    }
  }

  public notesToCalendar = (note: ICalendarNote) => {
    const formatDate = (date, time) =>
      moment
        .utc(moment(date).format('DD-MM-YYYY ') + time, 'DD-MM-YYYY HH:mm')
        .toDate();

    const includesHours = Boolean(note.start);
    const start = includesHours
      ? formatDate(note.date, note.start)
      : moment(note.date).toDate();
    const end = includesHours ? formatDate(note.date, note.end) : undefined;

    return {
      note,
      color: {
        primary: '#FFECB3',
        secondary: '#FFECB3',
      },
      cssClass: 'CalendarNote',
      start,
      end,
      title: note.title,
      allDay: !includesHours,
      draggable: this.canDragNote(note),
      resizable: {
        beforeStart: false,
        afterEnd: this.canResizeNote(note),
      },
    };
  };

  public timeBlockToCalendar = (block: ICalendarTimeBlock) => {
    const title =
      `${block.reason || ''}`.trim() ||
      this.translate.instant('CALENDAR.TIME_BLOCKS.BLOCKED');

    return {
      timeBlock: block,
      color: {
        primary: '#dc2626',
        secondary: 'rgba(248, 113, 113, 0.22)',
      },
      cssClass: 'CalendarTimeBlock',
      start: moment(block.start).toDate(),
      end: moment(block.end).toDate(),
      title,
      draggable: false,
      resizable: {
        beforeStart: false,
        afterEnd: false,
      },
      meta: {
        type: 'calendar_time_block',
        timeBlock: block,
      },
    };
  };

  public changeDay(date: Date) {
    this.viewDate = date;
    this.view = this.isMobileView ? CalendarView.Day : CalendarView.Week;
  }

  public openEvent($event: any) {
    const timeBlock = $event.timeBlock || $event.meta?.timeBlock || null;
    if (timeBlock) {
      this.openTimeBlockEditor(timeBlock);
      return;
    }

    const appointment = $event.appointment;
    if (appointment) {
      if (this.isMobileView) {
        return;
      }

      this.dialogService.open(AppointmentComponent, {
        closable: true,
        modal: true,
        width: '80%',
        data: {
          calendar: this.calendars,
          appointments_id: $event.appointment.id,
          updateAppointment: () =>
            this.loadCalendarItems(
              {
                appointments: true,
              },
              { silent: true },
            ),
        },
        header:
          appointment.name?.trim() ||
          this.translate.instant('APPOINTMENT.TITLE'),
      });
    }

    if ($event.note) {
      const ref = this.dialogService.open(FormCalendarNoteComponent, {
        closable: true,
        modal: true,
        width: '80%',
        inputValues: {
          payload: $event.note,
          onUpdate: () => {
            ref.close();
            this.loadCalendarItems({ notes: true });
          },
        },
        header: $event.note.title,
      });
    }
  }

  public openTimeBlockEditor(timeBlock: ICalendarTimeBlock): void {
    if (!this.hasPermission('CALENDAR_TIME_BLOCKS.UPDATE') &&
        !this.hasPermission('CALENDAR_TIME_BLOCKS.DELETE')) {
      return;
    }

    this.editingTimeBlock = timeBlock;
    this.editingTimeBlockCalendarId = Number(timeBlock.calendars_id || 0) || null;
    this.editingTimeBlockReason = timeBlock.reason || '';
    this.editingTimeBlockNotes = timeBlock.notes || '';
    this.editTimeBlockDialogVisible = true;
  }

  public get editingTimeBlockRangeLabel(): string {
    if (!this.editingTimeBlock) return '';
    return `${moment(this.editingTimeBlock.start).format('DD/MM/YYYY HH:mm')} - ${moment(
      this.editingTimeBlock.end,
    ).format('HH:mm')}`;
  }

  public cancelTimeBlockEdition(): void {
    this.editTimeBlockDialogVisible = false;
    this.editingTimeBlock = null;
    this.editingTimeBlockCalendarId = null;
    this.editingTimeBlockReason = '';
    this.editingTimeBlockNotes = '';
    this.savingEditedTimeBlock = false;
    this.deletingTimeBlock = false;
  }

  public updateEditingTimeBlock(): void {
    if (
      !this.editingTimeBlock?.id ||
      !this.editingTimeBlockCalendarId ||
      this.savingEditedTimeBlock ||
      !this.hasPermission('CALENDAR_TIME_BLOCKS.UPDATE')
    ) {
      return;
    }

    this.savingEditedTimeBlock = true;
    this.calendarTimeBlocksService
      .update(this.customers_id, this.site, Number(this.editingTimeBlock.id), {
        calendars_id: this.editingTimeBlockCalendarId,
        start: this.editingTimeBlock.start,
        end: this.editingTimeBlock.end,
        reason: this.editingTimeBlockReason,
        notes: this.editingTimeBlockNotes,
      })
      .subscribe({
        next: () => {
          this.savingEditedTimeBlock = false;
          this.cancelTimeBlockEdition();
          this.loadCalendarItems({ appointments: true });
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SAVED'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.UPDATED'),
          });
        },
        error: () => {
          this.savingEditedTimeBlock = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.UPDATE_ERROR'),
          });
        },
      });
  }

  public confirmDeleteEditingTimeBlock(): void {
    if (!this.editingTimeBlock?.id || this.deletingTimeBlock) {
      return;
    }

    this.confirmationService.confirm({
      header: this.translate.instant('CALENDAR.TIME_BLOCKS.DELETE_TITLE'),
      message: this.translate.instant('CALENDAR.TIME_BLOCKS.DELETE_CONFIRM'),
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.deleteEditingTimeBlock(),
    });
  }

  private deleteEditingTimeBlock(): void {
    if (!this.editingTimeBlock?.id || !this.hasPermission('CALENDAR_TIME_BLOCKS.DELETE')) {
      return;
    }

    this.deletingTimeBlock = true;
    this.calendarTimeBlocksService
      .remove(this.customers_id, this.site, Number(this.editingTimeBlock.id))
      .subscribe({
        next: () => {
          this.deletingTimeBlock = false;
          this.cancelTimeBlockEdition();
          this.loadCalendarItems({ appointments: true });
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SAVED'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.DELETED'),
          });
        },
        error: () => {
          this.deletingTimeBlock = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.DELETE_ERROR'),
          });
        },
      });
  }

  public handleSegmentClicked(object, calendar?: ICalendar): void {
    this.openSelectionDecision(object, calendar);
  }

  public onDesktopSegmentPointerDown(
    event: PointerEvent,
    segmentDate: Date,
    segmentHeight: number,
    calendar?: ICalendar,
  ): void {
    if (
      (!this.hasPermission('APPOINTMENTS.CREATE') &&
        !this.hasPermission('CALENDAR_TIME_BLOCKS.CREATE')) ||
      !this.calendars?.length
    ) {
      return;
    }

    const target = event.currentTarget as HTMLElement | null;
    this.desktopPointerState = {
      pointerId: event.pointerId,
      startDate: new Date(segmentDate),
      startY: event.clientY,
      segmentHeight: segmentHeight || 30,
      dragging: false,
      moved: false,
      calendar,
      target,
    };
    target?.setPointerCapture?.(event.pointerId);
  }

  @HostListener('document:pointermove', ['$event'])
  public onDesktopDocumentPointerMove(event: PointerEvent): void {
    if (
      !this.desktopPointerState ||
      event.pointerId !== this.desktopPointerState.pointerId ||
      this.isMobileView
    ) {
      return;
    }

    const deltaY = event.clientY - this.desktopPointerState.startY;
    const absDeltaY = Math.abs(deltaY);
    if (absDeltaY <= CalendarAppointmentsComponent.TAP_DRAG_THRESHOLD_PX) {
      return;
    }

    event.preventDefault();
    this.desktopPointerState.dragging = true;
    this.desktopPointerState.moved = true;

    const slotCount = Math.max(
      1,
      Math.ceil(absDeltaY / Math.max(this.desktopPointerState.segmentHeight, 1)),
    );
    const durationMinutes = slotCount * this.getPrecision();
    const anchor = moment(this.desktopPointerState.startDate)
      .seconds(0)
      .milliseconds(0);
    const startMoment =
      deltaY < 0 ? anchor.clone().subtract(durationMinutes, 'minutes') : anchor;
    const endMoment =
      deltaY < 0 ? anchor : anchor.clone().add(durationMinutes, 'minutes');

    this.applyDesktopSelectionRange(
      startMoment.toDate(),
      endMoment.toDate(),
      this.desktopPointerState.calendar,
      true,
    );
  }

  @HostListener('document:pointerup', ['$event'])
  @HostListener('document:pointercancel', ['$event'])
  public onDesktopDocumentPointerUp(event: PointerEvent): void {
    if (
      !this.desktopPointerState ||
      event.pointerId !== this.desktopPointerState.pointerId ||
      this.isMobileView
    ) {
      return;
    }

    this.desktopPointerState.target?.releasePointerCapture?.(event.pointerId);
    const pointerState = this.desktopPointerState;
    this.desktopPointerState = null;

    if (!pointerState.dragging && !pointerState.moved) {
      this.applyDesktopSelectionRange(
        pointerState.startDate,
        moment(pointerState.startDate).add(this.getPrecision(), 'minutes').toDate(),
        pointerState.calendar,
        false,
      );
    }

    if (this.selectedRangeStart && this.selectedRangeEnd) {
      this.selectionDialogVisible = true;
    }
  }

  public openSelectionDecision(start: Date, calendar?: ICalendar): void {
    const startMoment = moment(start).seconds(0).milliseconds(0);
    const endMoment = startMoment.clone().add(this.getPrecision(), 'minutes');
    this.applyDesktopSelectionRange(startMoment.toDate(), endMoment.toDate(), calendar, false);
    this.selectionDialogVisible = true;
  }

  private applyDesktopSelectionRange(
    start: Date,
    end: Date,
    calendar?: ICalendar,
    lockDuration = false,
  ): void {
    const availableCalendars = this.calendars || [];
    const startMoment = moment(start).seconds(0).milliseconds(0);
    const endMoment = moment(end).seconds(0).milliseconds(0);
    const rangeStart = moment.min(startMoment, endMoment);
    const rangeEnd = moment.max(startMoment, endMoment);
    const dayStart = rangeStart.clone().startOf('day').add(this.getDayStartHour(), 'hours');
    const dayEnd = rangeStart.clone().startOf('day').add(this.getDayEndHour(), 'hours');
    const normalizedStart = moment.max(rangeStart, dayStart);
    const normalizedEnd = moment.min(rangeEnd, dayEnd);

    this.selectedRangeStart = normalizedStart.toDate();
    this.selectedRangeEnd = normalizedEnd.isSameOrBefore(normalizedStart)
      ? normalizedStart.clone().add(this.getPrecision(), 'minutes').toDate()
      : normalizedEnd.toDate();
    this.selectedRangeLocksDuration = lockDuration;
    this.selectedRangeCalendarIds = calendar?.id
      ? [Number(calendar.id)]
      : availableCalendars.map((item) => Number(item.id)).filter(Boolean);
    this.timeBlockReason = '';
    this.timeBlockNotes = '';
    this.desktopSelectionEvent = this.createDesktopSelectionEvent();
    this.refreshCalendar();
  }

  private createDesktopSelectionEvent(): CalendarEvent | null {
    if (!this.selectedRangeStart || !this.selectedRangeEnd) return null;

    return {
      id: CalendarAppointmentsComponent.DESKTOP_SELECTION_EVENT_ID,
      title: this.selectionRangeLabel,
      start: this.selectedRangeStart,
      end: this.selectedRangeEnd,
      color: {
        primary: brandColor(600),
        secondary: brandColor(500, 0.14),
      },
      cssClass: 'CalendarSelection',
      meta: { selection: true },
      draggable: false,
      resizable: { beforeStart: false, afterEnd: false },
    };
  }

  public get selectionRangeLabel(): string {
    if (!this.selectedRangeStart || !this.selectedRangeEnd) return '';
    return `${moment(this.selectedRangeStart).format('DD/MM/YYYY HH:mm')} - ${moment(
      this.selectedRangeEnd,
    ).format('HH:mm')}`;
  }

  public toggleSelectionCalendar(calendar: ICalendar, checked: boolean): void {
    const calendarId = Number(calendar.id);
    if (!calendarId) return;
    if (checked) {
      this.selectedRangeCalendarIds = [
        ...new Set([...this.selectedRangeCalendarIds, calendarId]),
      ];
      return;
    }
    this.selectedRangeCalendarIds = this.selectedRangeCalendarIds.filter(
      (id) => id !== calendarId,
    );
  }

  public isSelectionCalendarChecked(calendar: ICalendar): boolean {
    return this.selectedRangeCalendarIds.includes(Number(calendar.id));
  }

  public createAppointmentFromSelection(): void {
    if (!this.selectedRangeStart || !this.selectedRangeEnd) return;
    const selectedCalendar = this.calendars.find(
      (calendar) => Number(calendar.id) === Number(this.selectedRangeCalendarIds[0]),
    );
    this.selectionDialogVisible = false;
    this.clearDesktopSelectionEvent();
    this.addAppointment(
      moment(this.selectedRangeStart).format('DD-MM-YYYY HH:mm'),
      selectedCalendar,
      moment(this.selectedRangeEnd).format('DD-MM-YYYY HH:mm'),
    );
  }

  public openTimeBlockDialogFromSelection(): void {
    if (!this.selectedRangeStart || !this.selectedRangeEnd) return;
    this.selectionDialogVisible = false;
    this.timeBlockDialogVisible = true;
  }

  public cancelSelection(): void {
    this.selectionDialogVisible = false;
    this.timeBlockDialogVisible = false;
    this.timeBlockReason = '';
    this.timeBlockNotes = '';
    this.selectedRangeStart = null;
    this.selectedRangeEnd = null;
    this.selectedRangeLocksDuration = false;
    this.selectedRangeCalendarIds = [];
    this.clearDesktopSelectionEvent();
  }

  private clearDesktopSelectionEvent(): void {
    this.desktopSelectionEvent = null;
    this.refreshCalendar();
  }

  public createTimeBlockFromSelection(): void {
    if (
      !this.selectedRangeStart ||
      !this.selectedRangeEnd ||
      !this.selectedRangeCalendarIds.length ||
      this.savingTimeBlock
    ) {
      return;
    }

    this.savingTimeBlock = true;
    this.calendarTimeBlocksService
      .createBulk(this.customers_id, this.site, {
        calendars_ids: this.selectedRangeCalendarIds,
        start: this.selectedRangeStart,
        end: this.selectedRangeEnd,
        reason: this.timeBlockReason,
        notes: this.timeBlockNotes,
      })
      .subscribe({
        next: () => {
          this.savingTimeBlock = false;
          this.timeBlockDialogVisible = false;
          this.clearDesktopSelectionEvent();
          this.loadCalendarItems({ appointments: true });
          this.messageService.add({
            severity: 'success',
            summary: this.translate.instant('COMMON.SAVED'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.SAVED'),
          });
        },
        error: () => {
          this.savingTimeBlock = false;
          this.messageService.add({
            severity: 'error',
            summary: this.translate.instant('COMMON.ERROR'),
            detail: this.translate.instant('CALENDAR.TIME_BLOCKS.ERROR'),
          });
        },
      });
  }

  public addAppointment(startDate?: string, calendar?: ICalendar, endDate?: string) {
    this.dialogService.open(FormCreateAppointmentComponent, {
      width: '74rem',
      breakpoints: {
        '1280px': '90vw',
        '1200px': '92vw',
        '960px': '92vw',
        '640px': '98vw',
      },
      contentStyle: {
        height: 'min(82vh, 56rem)',
        'max-height': '82vh',
        overflow: 'hidden',
      },
      closable: true,
      modal: true,
      data: {
        startDate,
        endDate,
        lockSelectedDuration: this.selectedRangeLocksDuration,
        patient: this.patient,
        treatments: this.treatments,
        selectedCalendar: calendar,
        calendar,
        callback: () => {
          this.loadCalendarItems({ appointments: true });
        },
      },
      header: this.translate.instant('APPOINTMENT.ADD'),
    });
  }

  public ngOnDestroy() {
    this.subscriptionAppointments.unsubscribe();
    this.subscriptionCalendar.unsubscribe();

    if (this.subscriptionTranslate) {
      this.subscriptionTranslate.unsubscribe();
    }
  }

  public loadCalendarItems(
    config,
    options?: { silent?: boolean },
  ) {
    if (!options?.silent) {
      this.loadingService.setLoading({ isLoading: true, message: 'Loading...' });
    }
    const eventObservables = [];
    const eventTypes = [];

    if (config.appointments) {
      eventObservables.push(this.loadAppointments());
      eventTypes.push('appointments');
    }

    if (config.notes && this.hasPermission('CALENDAR_NOTES.READ')) {
      eventObservables.push(this.loadNotes());
      eventTypes.push('notes');
    }

    if (config.appointments && this.hasPermission('CALENDAR_TIME_BLOCKS.READ')) {
      eventObservables.push(this.loadTimeBlocks());
      eventTypes.push('timeBlocks');
    }

    if (eventObservables.length === 0) {
      if (!options?.silent) {
        this.loadingService.stopLoading();
      }
      return;
    }

    forkJoin(eventObservables).subscribe({
      next: (results) => {
        results.forEach((result, index) => {
          if (eventTypes[index] === 'appointments') {
            this.appointments = result;
          } else if (eventTypes[index] === 'notes') {
            this.notes = result;
          } else if (eventTypes[index] === 'timeBlocks') {
            this.timeBlocks = result;
          }
        });

        this.refreshCalendar();
      },
      error: (error) => {
        if (!options?.silent) {
          this.loadingService.stopLoading();
        }
      },
      complete: () => {
        if (!options?.silent) {
          this.loadingService.stopLoading();
        }
      },
    });
  }

  public getEventsByCalendar(calendar: ICalendar) {
    return this.events.filter((event: any) => {
      if (event.appointment) {
        return event.appointment.calendars_id === calendar.id;
      }
      if (event.note) {
        return event.note.calendars_id === calendar.id;
      }
      if (event.timeBlock) {
        return event.timeBlock.calendars_id === calendar.id;
      }
      return false;
    });
  }

  public navigatePrevious(): void {
    const viewDate = moment(this.viewDate);
    let nextDate: moment.Moment;

    // En móvil con vista de semana, navegar por 3 días
    if (this.isMobileView && this.view === CalendarView.Week) {
      nextDate = viewDate.subtract(7, 'days');
    } else {
      nextDate = viewDate.subtract(1, this.view);
    }

    this.onAction({
      date: nextDate.toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  public navigateNext(): void {
    const viewDate = moment(this.viewDate);
    let nextDate: moment.Moment;

    // En móvil con vista de semana, navegar por 3 días
    if (this.isMobileView && this.view === CalendarView.Week) {
      nextDate = viewDate.add(7, 'days');
    } else {
      nextDate = viewDate.add(1, this.view);
    }

    this.onAction({
      date: nextDate.toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  public goToToday(): void {
    this.onAction({
      date: moment().toDate(),
      retrieve: { appointments: true, notes: true },
    });
  }

  public goToDate(date: Date): void {
    this.onAction({
      date: date,
      retrieve: { appointments: true, notes: true },
    });
  }

  public ngOnInit(): void {
    super.ngOnInit();
    this.isMobileView = this.isMobileDevice();

    this.locale = this.translate.currentLang;
    if (this.date) {
      this.viewDate = moment(this.date).toDate();
    }
    if (this.isMobileView && this.view === CalendarView.Month) {
      this.view = CalendarView.Week;
    }
    //this.setSchedule();
    this.loadCalendarItems({ appointments: true, notes: true });

    this.subscriptionAppointments = this.appointmentsService
      .infoAppointments$()
      .subscribe(() => {
        this.loadCalendarItems({ appointments: true });
      });

    this.subscriptionCalendar = this.calendarAppointmentsService
      .getCalendar$()
      .subscribe((calendars: ICalendar[]) => {
        this.calendars = calendars;
        this.loadCalendarItems({ appointments: true, notes: true });
      });

    this.subscriptionTranslate = this.translate.onLangChange.subscribe(() => {
      this.locale = this.translate.currentLang;
    });
  }

  public ngOnChanges(changes: any) {
    // Si cambian los filtros, recargar citas
    if (changes.settings != undefined && !changes.settings.firstChange) {
      const prev = changes.settings.previousValue as ICalendarSettings;
      const curr = changes.settings.currentValue as ICalendarSettings;

      // Recargar si cambian los filtros o el modo de color
      const filtersChanged =
        JSON.stringify(prev?.filters) !== JSON.stringify(curr?.filters);
      const colorModeChanged =
        prev?.display?.colorMode !== curr?.display?.colorMode;

      if (filtersChanged || colorModeChanged) {
        this.loadCalendarItems({ appointments: true });
      } else {
        // Para cambios de view/display solo refrescar la UI
        this.refresh.next();
      }
    }
  }
}
